import { inject, Service } from '@angular/core';
import { PlatformService } from '@core/platform/platform.service';

/**
 * StorageService — isolated, SSR-safe key/value store for /v2.
 *
 * - All access is guarded by PlatformService (no-op on the server).
 * - Values are JSON-serialised.
 * - Defaults to `localStorage`; pass `session = true` for `sessionStorage`.
 * - No shared state with v1's StorageService.
 */
@Service()
export class StorageService {
  private readonly platform = inject(PlatformService);

  private store(session: boolean): Storage | null {
    if (!this.platform.isBrowser) return null;
    try {
      return session ? this.platform.window!.sessionStorage : this.platform.window!.localStorage;
    } catch {
      return null;
    }
  }

  get<T>(key: string, session = false): T | null {
    const raw = this.store(session)?.getItem(key);
    if (raw == null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  set<T>(key: string, value: T, session = false): void {
    try {
      this.store(session)?.setItem(key, JSON.stringify(value));
    } catch {
      /* quota / private-mode — fail silently, v2 always has in-memory fallbacks */
    }
  }

  remove(key: string, session = false): void {
    try {
      this.store(session)?.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  /** Remove several keys at once (e.g. a sign-out purge of user-scoped data). */
  removeMany(keys: readonly string[], session = false): void {
    for (const key of keys) this.remove(key, session);
  }
}
